/* ------------------------------------------------------------------
   EmailLists.js — Marketing → Email Marketing → Lists
   Mailing lists are curated groups of CRM contacts. Campaigns can target
   a list; bio email-capture can add subscribers to one. Two views:
     'list'    — all lists (create / rename / delete)
     'members' — one list's members (+ add from CRM / remove)
------------------------------------------------------------------ */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const ax = axios.create({ withCredentials: true });

export default function EmailLists() {
  const [view, setView]   = useState('list');   // 'list' | 'members'
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]     = useState(null);

  const [active, setActive] = useState(null);    // list being viewed

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await ax.get('/api/email/lists'); setLists(data.lists || []); }
    catch (e) { console.error('load lists', e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  /* ── create ── */
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  async function createList(e) {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try { await ax.post('/api/email/lists', { name }); setNewName(''); load(); flash('List created'); }
    catch (err) { flash(err.response?.data?.error || 'Could not create list', false); }
    finally { setCreating(false); }
  }

  async function renameList(l) {
    const name = window.prompt('Rename list', l.name);
    if (name == null) return;
    if (!name.trim()) { flash('Name cannot be empty', false); return; }
    try { await ax.put(`/api/email/lists/${l.id}`, { name: name.trim() }); load(); }
    catch { flash('Rename failed', false); }
  }

  async function deleteList(l) {
    if (!window.confirm(`Delete "${l.name}"? Contacts stay in your CRM — only the list is removed.`)) return;
    try { await ax.delete(`/api/email/lists/${l.id}`); load(); flash('List deleted'); }
    catch { flash('Delete failed', false); }
  }

  function openMembers(l) { setActive(l); setView('members'); }

  /* ───────── MEMBERS VIEW ───────── */
  if (view === 'members' && active) {
    return <MembersView list={active} onBack={() => { setView('list'); setActive(null); load(); }} flash={flash} />;
  }

  /* ───────── LIST VIEW ───────── */
  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Lists</h1>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Curated groups of contacts you can target in campaigns and automations.</p>
      </div>

      {msg && <Banner msg={msg} />}

      <form onSubmit={createList} style={{ display: 'flex', gap: 10, marginBottom: 18, maxWidth: 520 }}>
        <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New list name (e.g. Newsletter Subscribers)" style={inputStyle} />
        <button type="submit" disabled={creating || !newName.trim()} style={{ ...primaryBtn, whiteSpace: 'nowrap', opacity: (creating || !newName.trim()) ? 0.6 : 1 }}>{creating ? 'Adding…' : '+ Add list'}</button>
      </form>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : lists.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
            No lists yet. Create one above, then add contacts from your CRM.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left' }}>
                <Th>Name</Th><Th>Contacts</Th><Th>Created</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {lists.map(l => (
                <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <Td>
                    <button onClick={() => openMembers(l)} style={linkBtn}>{l.name}</button>
                    {l.description && <div style={{ fontSize: 12, color: '#9ca3af' }}>{l.description}</div>}
                  </Td>
                  <Td><span style={{ fontWeight: 600 }}>{(l.member_count || 0).toLocaleString()}</span></Td>
                  <Td><span style={{ color: '#6b7280' }}>{String(l.created_at || '').slice(0, 10)}</span></Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button onClick={() => openMembers(l)} style={miniBtn}>Manage</button>
                      <button onClick={() => renameList(l)} style={miniBtn}>Rename</button>
                      <button onClick={() => deleteList(l)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Delete</button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ===================== members sub-view ===================== */
function MembersView({ list, onBack, flash }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await ax.get(`/api/email/lists/${list.id}/members`, { params: { search } }); setMembers(data.members || []); }
    catch (e) { console.error('load members', e); }
    finally { setLoading(false); }
  }, [list.id, search]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  async function removeMember(c) {
    if (!window.confirm(`Remove ${c.email} from "${list.name}"?`)) return;
    try { await ax.delete(`/api/email/lists/${list.id}/members/${c.id}`); load(); }
    catch { flash('Remove failed', false); }
  }

  return (
    <div>
      <button onClick={onBack} style={{ ...ghostBtn, marginBottom: 14 }}>← Back to lists</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>{list.name}</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>{members.length.toLocaleString()} contact{members.length === 1 ? '' : 's'} in this list.</p>
        </div>
        <button onClick={() => setShowAdd(true)} style={primaryBtn}>+ Add contacts</button>
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members…" style={{ ...inputStyle, maxWidth: 320, marginBottom: 14 }} />

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : members.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
            {search ? 'No members match your search.' : 'No contacts yet. Click "Add contacts" to pull from your CRM.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left' }}>
                <Th>Name</Th><Th>Email</Th><Th>Status</Th><Th>Added</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {members.map(c => (
                <tr key={c.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <Td>{c.name || <span style={{ color: '#9ca3af' }}>—</span>}</Td>
                  <Td>{c.email}</Td>
                  <Td><span style={{ fontSize: 12, color: '#6b7280', textTransform: 'capitalize' }}>{c.status}</span></Td>
                  <Td><span style={{ color: '#6b7280' }}>{String(c.added_at || '').slice(0, 10)}</span></Td>
                  <Td><div style={{ display: 'flex', justifyContent: 'flex-end' }}><button onClick={() => removeMember(c)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Remove</button></div></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddContactsModal list={list} existing={new Set(members.map(m => m.id))} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} flash={flash} />}
    </div>
  );
}

/* ===================== add-contacts modal ===================== */
/* Pull emails (+ optional names) out of pasted text or a CSV. Handles
   "email", "email, name", "name, email", and comma-separated lists. */
function parseContacts(text) {
  const map = new Map();
  String(text || '').split(/[\r\n]+/).forEach(line => {
    const emails = line.match(/[^\s,;<>"]+@[^\s,;<>"]+\.[^\s,;<>"]+/g) || [];
    if (emails.length === 1) {
      const email = emails[0].toLowerCase();
      const name = line.replace(emails[0], '').replace(/[,;\t<>"]/g, ' ').trim();
      if (!map.has(email)) map.set(email, name || null);
    } else {
      emails.forEach(e => { const email = e.toLowerCase(); if (!map.has(email)) map.set(email, null); });
    }
  });
  return [...map.entries()].map(([email, name]) => ({ email, name }));
}

function AddContactsModal({ list, existing, onClose, onAdded, flash }) {
  const [tab, setTab] = useState('crm');   // 'crm' | 'import'

  /* ── CRM tab ── */
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [picked, setPicked]     = useState(new Set());
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (tab !== 'crm') return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await ax.get('/api/crm/contacts', { params: { search } });
        setContacts(data.contacts || data || []);
      } catch (e) { console.error('load crm', e); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [search, tab]);

  const toggle = id => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectable = contacts.filter(c => !existing.has(c.id));

  async function addFromCrm() {
    const ids = [...picked];
    if (!ids.length) return;
    setSaving(true);
    try {
      const { data } = await ax.post(`/api/email/lists/${list.id}/members`, { contactIds: ids });
      flash(`Added ${data.added} contact${data.added === 1 ? '' : 's'}`);
      onAdded();
    } catch (err) { flash(err.response?.data?.error || 'Could not add contacts', false); }
    finally { setSaving(false); }
  }

  /* ── Import tab ── */
  const [raw, setRaw] = useState('');
  const parsed = parseContacts(raw);

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRaw(prev => (prev ? prev + '\n' : '') + String(reader.result || ''));
    reader.readAsText(file);
    e.target.value = '';
  }

  async function addFromImport() {
    if (!parsed.length) return;
    setSaving(true);
    try {
      const { data } = await ax.post(`/api/email/lists/${list.id}/import`, { contacts: parsed });
      const bits = [`Added ${data.added} to "${list.name}"`];
      const already = (data.imported || 0) - (data.added || 0);
      if (already > 0) bits.push(`${already} already in list`);
      if (data.invalid > 0) bits.push(`${data.invalid} invalid skipped`);
      flash(bits.join(' · '));
      onAdded();
    } catch (err) { flash(err.response?.data?.error || 'Could not import contacts', false); }
    finally { setSaving(false); }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Add contacts to "{list.name}"</div>
          <button onClick={onClose} style={{ ...miniBtn, border: 'none' }}>✕</button>
        </div>

        {/* tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0' }}>
          {[['crm', 'From CRM'], ['import', 'Import / paste']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13.5, fontWeight: 700, borderBottom: `2px solid ${tab === id ? '#2563eb' : 'transparent'}`,
              color: tab === id ? '#2563eb' : '#6b7280',
            }}>{label}</button>
          ))}
        </div>
        <div style={{ borderBottom: '1px solid #f1f5f9' }} />

        {tab === 'crm' ? (
          <>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search your CRM…" style={inputStyle} autoFocus />
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
              ) : selectable.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                  {contacts.length ? 'Everyone matching is already in this list.' : 'No contacts in your CRM yet — use “Import / paste”.'}
                </div>
              ) : selectable.map(c => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}>
                  <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} style={{ width: 16, height: 16 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.name || c.email}</div>
                    {c.name && <div style={{ fontSize: 12, color: '#9ca3af' }}>{c.email}</div>}
                  </div>
                </label>
              ))}
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>{picked.size} selected</span>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} style={ghostBtn}>Cancel</button>
                <button onClick={addFromCrm} disabled={saving || !picked.size} style={{ ...primaryBtn, opacity: (saving || !picked.size) ? 0.6 : 1 }}>{saving ? 'Adding…' : `Add ${picked.size || ''}`}</button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '16px 20px' }}>
              <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Paste emails</label>
              <p style={{ fontSize: 12.5, color: '#9ca3af', margin: '4px 0 8px' }}>
                One per line, or comma-separated. Optional name: <code>jane@acme.com, Jane Doe</code>. New contacts are also saved to your CRM.
              </p>
              <textarea
                value={raw} onChange={e => setRaw(e.target.value)} autoFocus
                placeholder={'jane@acme.com, Jane Doe\nsam@acme.com\n…'}
                style={{ ...inputStyle, minHeight: 150, resize: 'vertical', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                <label style={{ ...ghostBtn, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                  ⬆ Upload CSV
                  <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} style={{ display: 'none' }} />
                </label>
                {raw && <button onClick={() => setRaw('')} style={{ ...miniBtn }}>Clear</button>}
                <span style={{ marginLeft: 'auto', fontSize: 13, color: parsed.length ? '#059669' : '#9ca3af', fontWeight: 600 }}>
                  {parsed.length} valid email{parsed.length === 1 ? '' : 's'} detected
                </span>
              </div>
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={onClose} style={ghostBtn}>Cancel</button>
              <button onClick={addFromImport} disabled={saving || !parsed.length} style={{ ...primaryBtn, opacity: (saving || !parsed.length) ? 0.6 : 1 }}>
                {saving ? 'Importing…' : `Import ${parsed.length || ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── shared styles ── */
const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box' };
const primaryBtn = { padding: '9px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghostBtn   = { padding: '9px 14px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const miniBtn    = { padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const linkBtn    = { background: 'none', border: 'none', padding: 0, color: '#2563eb', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
const overlay    = { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: 16 };
const modal      = { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' };
const Th = ({ children }) => <th style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: '11px 14px', color: '#111827' }}>{children}</td>;
const Banner = ({ msg }) => (
  <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
    background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#059669' : '#dc2626',
    border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}` }}>{msg.text}</div>
);
